/**
 * Revoke access for an accepted support agent (workspace or tenant scope).
 */

import { useCallback, useState } from "react";

import { revokeAgentAccess } from "@/api/directoryApi";

/**
 * @param {{
 *   agentEmail: string,
 *   workspaceName?: string,
 *   tenantWideOnly?: boolean,
 *   onRevoked?: () => void | Promise<void>,
 * }} props
 */
export default function RevokeAccessButton({
  agentEmail,
  workspaceName,
  tenantWideOnly = false,
  onRevoked,
}) {
  const [phase, setPhase] = useState(
    /** @type {"idle"|"choose"|"confirm_workspace"|"confirm_tenant"|"loading"} */ ("idle")
  );
  const [err, setErr] = useState("");

  const workspaceLabel = (workspaceName || "this workspace").trim();

  const handleRevoke = useCallback(
    async (scope) => {
      setPhase("loading");
      setErr("");
      try {
        await revokeAgentAccess({ email: agentEmail, scope });
        await onRevoked?.();
        setPhase("idle");
      } catch (ex) {
        setErr(ex instanceof Error ? ex.message : "Revoke failed.");
        setPhase("idle");
      }
    },
    [agentEmail, onRevoked]
  );

  if (phase === "choose" && !tenantWideOnly) {
    return (
      <div className="flex flex-col items-end gap-1.5">
        <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400">Remove access:</p>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => setPhase("confirm_workspace")}
            className="rounded px-2 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200 hover:bg-amber-50 dark:text-amber-300 dark:ring-amber-800/60 dark:hover:bg-amber-950/40"
          >
            This workspace
          </button>
          <button
            type="button"
            onClick={() => setPhase("confirm_tenant")}
            className="rounded px-2 py-1 text-[11px] font-medium text-rose-700 ring-1 ring-rose-200 hover:bg-rose-50 dark:text-rose-400 dark:ring-rose-800/60 dark:hover:bg-rose-950/40"
          >
            Entire system
          </button>
          <button
            type="button"
            onClick={() => {
              setPhase("idle");
              setErr("");
            }}
            className="text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (phase === "confirm_workspace" && !tenantWideOnly) {
    return (
      <div className="flex flex-col items-end gap-1">
        <p className="max-w-[14rem] text-right text-[10px] leading-snug text-slate-500 dark:text-slate-300">
          Remove from <strong className="text-slate-700 dark:text-slate-100">{workspaceLabel}</strong> only?
        </p>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => void handleRevoke("workspace")}
            className="rounded bg-rose-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-rose-700"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setPhase("choose")}
            className="text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  if (phase === "confirm_tenant" || (phase === "choose" && tenantWideOnly)) {
    return (
      <div className="flex flex-col items-end gap-1">
        <p className="max-w-[14rem] text-right text-[10px] leading-snug text-slate-500 dark:text-slate-300">
          Remove from <strong className="text-slate-700 dark:text-slate-100">all workspaces</strong> and deactivate
          the account?
        </p>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => void handleRevoke("tenant")}
            className="rounded bg-rose-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-rose-700"
          >
            Confirm remove
          </button>
          <button
            type="button"
            onClick={() => {
              setPhase("idle");
              setErr("");
            }}
            className="text-[11px] text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => {
          setPhase(tenantWideOnly ? "confirm_tenant" : "choose");
          setErr("");
        }}
        disabled={phase === "loading"}
        className="rounded px-2 py-1 text-[11px] font-medium text-rose-600 transition hover:bg-rose-50 hover:text-rose-700 disabled:opacity-60 dark:text-rose-400 dark:hover:bg-rose-950/40 dark:hover:text-rose-300"
      >
        {phase === "loading" ? (
          <i className="ri-loader-4-line animate-spin" />
        ) : tenantWideOnly ? (
          "Revoke all access"
        ) : (
          "Revoke access"
        )}
      </button>
      {err ? (
        <p className="max-w-[12rem] text-right text-[10px] leading-snug text-rose-600 dark:text-rose-400" role="alert">
          {err}
        </p>
      ) : null}
    </div>
  );
}
